import _set from 'lodash/set';
import _get from 'lodash/get';
import { Pixall, Google } from '../analytics/index.js';
import { addListingTriggerData, getInventoryOwnerById, isPremiumSpotlight, isVDP } from '../utilities/index.js';
import { DataLayerTransformer, inventoryTransformer, productConfigTransformer as pixallProductConfigTransformer } from '../transformers/pixall/index.js';
import { productTileClickHelper, resourceClickHelper, ownerWebsiteClickHelper, mapPartnerTagToGA, sanitizeValue, partnerResourceHelper } from './helpers/index.js';
import { valueEventCode } from '../constants/index.js';
import { inventoryTransformer as googleInventoryTransformer, productConfigTransformer as googleProductConfigTransformer } from '../transformers/google/index.js';
const {
  transformProductConfig
} = googleProductConfigTransformer;
const {
  getProductConfiguration
} = pixallProductConfigTransformer;

/**
 * @summary Sends events for print tracking when user ctrl/cmd + P
 * @cmp prnt_hlgt
 */
export function printTrack(_ref, option) {
  let {
    data = {},
    state,
    dataLayer
  } = _ref;
  const {
    inventoryId,
    pixallData = {}
  } = data;
  const cmp = 'prnt_hlgt';
  const pageId = DataLayerTransformer.getPageId(dataLayer);
  const inventory = getInventoryOwnerById(state, inventoryId);
  Pixall.sendClickEvent({
    pixallData: {
      ...pixallData,
      clickTypeToListingType: isVDP(dataLayer)
    },
    label: `Print - ${cmp} - ${pageId}`,
    eventSource: 'vehicle',
    eventType: 'print',
    feature: 'Drive the 9',
    inventory
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'dealer',
    eventAction: 'print',
    eventResult: 'cta::print'
  }, option);
  return {
    cmp
  };
}

/**
 * @summary Sends Adobe/Pixall Click event for share click
 * @cmp shr_fr
 */
export function shareClick(_ref2, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref2;
  const {
    inventoryId,
    par,
    type,
    action = 'dealer',
    includePageId = true,
    interactive = false,
    wasSticky,
    pixallData = {}
  } = data;
  const pageId = dataLayer?.page?.brandPg ?? dataLayer?.page?.pg ?? '';
  const inventory = getInventoryOwnerById(state, inventoryId);
  let eventData = {};
  if (action === 'my-at' || action === 'my-kbb') {
    eventData = {
      vehicle: {
        catalog: dataLayer.catalog,
        chromeStyleId: inventory.styleId,
        vehicleId: inventory.id?.toString(),
        listingId: inventory.id?.toString(),
        year: inventory.year?.toString(),
        makeId: inventory.make?.code,
        makeName: inventory.make?.name,
        modelId: inventory.model?.code,
        modelName: inventory.model?.name,
        seriesId: inventory.series,
        trimId: inventory.trim?.name,
        bodyStyle: Array.isArray(inventory.styles) ? inventory.styles[0]?.code : inventory.styles?.code,
        subCategory: dataLayer.consumerInsights?.pe_subcat,
        condition: inventory.listingTypes && inventory.listingTypes[0].code,
        mileage: inventory.mileage?.value,
        fuelType: inventory.fuelType?.name,
        fuelEconomy: `${inventory.mpgCity} City/ ${inventory.mpgHighway} Highway`,
        price: inventory.pricingDetail?.salePrice?.toString(),
        sellerType: inventory.owner?.privateSeller ? 'private' : 'dealer',
        ...transformProductConfig(inventory),
        description: inventory.description?.label ? 'yes' : 'no'
      }
    };
  }
  let cmp = 'shr_fr'; // Share value for icon
  let eventType = 'click'; // event type value for icon
  let eventResult; // event type passed to google
  let resultOrigin = 'article';
  const cdl = {}; // common data layer value
  if (inventoryId) {
    resultOrigin = 'vin';
  }
  switch (type) {
    case 'email':
      cmp = 'shr_eml';
      eventResult = `cta::share_email::${resultOrigin}`;
      _set(cdl, 'eventProperties.event.valueEventCode', getProductConfiguration(inventory).includes('my-wallet') ? valueEventCode.LEAD_WITH_MY_WALLET : valueEventCode.SHARE_CAR);
      break;
    case 'text':
      cmp = 'shr_txt';
      eventResult = `cta::share_text::${resultOrigin}`;
      _set(cdl, 'eventProperties.event.valueEventCode', valueEventCode.SHARE_CAR);
      break;

    // Next Best Action - tells that the CTA is from the Email Confirmation Next Steps modal
    case 'nba':
      eventResult = `cta::share::${resultOrigin}::nba`;
      break;
    default:
      eventResult = 'icon::share';
      break;
  }
  if (cmp === 'shr_eml' || cmp === 'shr_txt') {
    eventType = 'share';
  }
  const labelSuffix = includePageId ? ` - ${pageId}` : '';
  cmp += wasSticky ? '_stky' : '';
  const listingType = isPremiumSpotlight(par) ? 'highlight' : undefined;
  addListingTriggerData(par, cdl);
  Pixall.sendClickEvent({
    pixallData: {
      ...pixallData,
      clickTypeToListingType: isVDP(dataLayer)
    },
    label: `${cmp}${labelSuffix}`,
    listingType,
    pageId,
    eventType,
    eventSource: 'vehicle',
    inventory,
    cdl,
    par
  }, dataLayer, option);
  eventResult += wasSticky ? '::sticky-header' : '';
  Google.sendClickEvent({
    eventElement: action,
    eventAction: type === 'icon' ? eventType : 'share',
    eventNonInteractive: interactive,
    eventResult,
    eventData
  }, option);
  return {
    cmp,
    par,
    listingType,
    cdl
  };
}

/**
 * @summary Sends Adobe/Pixall Click event for financeWithUsLink
 * @cmp apply_crd_vw_wb_btn
 */
export function financeWithUsLink(_ref3, option) {
  let {
    data = {},
    event,
    state,
    ...rest
  } = _ref3;
  let googleData = {};
  const {
    cmp,
    inventoryId
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const additionalResources = inventory.resources?.additionalResources ?? [];
  const partnerResource = additionalResources.find(resource => resource?.cmp === cmp);
  const eventResult = mapPartnerTagToGA({
    name: 'financeWithUs',
    heading: `additional-resources::${partnerResource?.title}`,
    subhead: partnerResource?.link?.label,
    cta: event.target?.parentNode?.nodeName === 'A' ? '' : 'module',
    title: inventory.ownerName || inventory.owner?.name
  });
  if (eventResult) {
    googleData = {
      eventElement: 'partner',
      eventResult
    };
  }
  return ownerWebsiteClickHelper({
    data: {
      ...data
    },
    googleData,
    event,
    state,
    ...rest
  }, option);
}

/**
 * @summary Handles AT CapitalOne additional resource
 * @cmp get_loan_txt
 */
export const capOneResourceClick = resourceClickHelper({
  cmp: 'get_loan_txt',
  epn: 'CapitalOne'
});

/**
 * @summary Handles AT Featured Partners Click
 * @data example {
 *  name: 'capitalone',
    heading: 'Featured Partner',
    subhead: 'calc_pymt',
    cta: 'Get Pre-Qualified',
 * }
 */

export function featuredPartnerClick(metadata, option) {
  const {
    data,
    dataLayer
  } = metadata;
  const {
    subhead
  } = data;
  const partnerEventResult = mapPartnerTagToGA(data);
  if (partnerEventResult) {
    Google.sendClickEvent({
      eventElement: 'partner',
      eventResult: partnerEventResult
    }, option);
  }
  Pixall.sendClickEvent({
    applicationType: 'loan',
    eventSource: 'application',
    eventType: 'click',
    fullURL: window.location.href,
    label: 'get_loan',
    pageType: subhead === 'calc_pymt' || subhead === 'calc_afd' || subhead === 'calc_lse_loan' ? 'financial' : 'search',
    vendor: data?.name,
    hasListingType: false,
    cdl: {
      eventProperties: {
        event: {
          valueEventCode: 'D52F'
        }
      }
    }
  }, dataLayer, option);
  return {};
}

/**
 * @summary Sends a Google clickEvent for KBB logo in AT HP Redesign - Autotrader Advantage Section
 */
export function kbbLogoClick(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    data
  } = metadata;
  const {
    ctaCopy
  } = data;
  Google.sendClickEvent({
    eventElement: 'home',
    eventAction: 'click',
    eventNonInteractive: false,
    eventResult: `cta::${sanitizeValue(ctaCopy)}`
  }, option);
  return {};
}

/**
 * @summary Handles AT Featured Partners Impression
 * @data example {
 *  name: 'capitalone',
    heading: 'Featured Partner',
    subhead: 'calc_pymt',
    cta: 'Get Pre-Qualified',
 * }
 */

export function featuredPartnerImpression(metadata, option) {
  const {
    data,
    dataLayer = {},
    state
  } = metadata;
  const {
    subhead
  } = data;
  const {
    name: partner
  } = data;
  const googleData = {
    eventElement: 'partner',
    eventNonInteractive: false,
    eventResult: mapPartnerTagToGA(data)
  };
  if (['rootInsurance', 'capitalone', 'experian'].includes(partner)) {
    const {
      inventoryId
    } = data;
    const inventory = getInventoryOwnerById(state, inventoryId);
    const customApplicationType = {
      capitalone: 'loan',
      rootInsurance: null,
      experian: 'credit'
    };
    const customLabel = {
      capitalone: 'get_loan',
      rootInsurance: 'insurance_root-insurance_additional-resources',
      experian: 'credit-report::experian::get-your-fico-score-for-free'
    };
    const customEventSource = {
      capitalone: 'application',
      rootInsurance: 'insurance',
      experian: 'application'
    };
    const customPageType = {
      capitalone: subhead === 'calc_pymt' || subhead === 'calc_afd' || subhead === 'calc_lse_loan' ? 'financial' : 'search',
      rootInsurance: 'vdp',
      experian: 'vdp'
    };
    const pixallPayload = {
      applicationType: customApplicationType[partner],
      clickType: inventory?.clickType,
      label: customLabel[partner],
      eventSource: customEventSource[partner],
      eventType: 'impression',
      ...inventoryTransformer.getInventoryData(inventory),
      vehicleType: (inventory?.listingType || '').toUpperCase(),
      vendor: partner === 'rootInsurance' ? 'root-insurance' : partner,
      hasListingType: partner !== 'capitalone'
    };
    const pageId = dataLayer?.page?.pg;
    if (pageId !== 'fyc_vdp') {
      pixallPayload.pageType = customPageType[partner];
    }
    Pixall.sendImpression({
      dataLayer
    }, pixallPayload, option);
  }
  if (googleData.eventResult) {
    Google.sendImpressions(metadata, googleData, option);
  }
  return {};
}

/**
 * @summary Handles AT Sell Car resource click
 * @cmp ny_syc
 */
export const sellCarClick = resourceClickHelper({
  cmp: 'ny_syc'
});

/**
 * @summary Handles KBB Instant Cash Offer
 * @cmp ns_tim
 */
export const kbbInstantCashClick = resourceClickHelper({
  cmp: 'ns_tim'
});

/**
 * @summary Handles KBB Experian Credit Report
 * @cmp ny_syc
 */
export const creditReportClick = resourceClickHelper({
  cmp: 'ny_syc',
  epn: 'experian'
});

/**
 * @summary Handles KBB Carchex Warranty
 * @cmp get_loan_txt
 */
export const warrantyResourceClick = resourceClickHelper({
  cmp: 'get_loan_txt',
  epn: 'carchex'
});

/**
 * @summary Handles root insurance
 * @cmp get_quote_txt
 */
export const rootInsuranceClick = resourceClickHelper({
  cmp: 'get_quote_txt',
  epn: 'Root-insurance'
});

/**
 * @summary Handles product tile click
 * @cmp ec_pa_lgo
 */
export function productTileClick(metadata, option) {
  const response = productTileClickHelper(metadata, option);
  return response;
}
export function preQualCTAClick(_ref4, option) {
  let {
    dataLayer,
    data = {},
    state
  } = _ref4;
  const {
    epn,
    uiContext,
    pixallData = {},
    inventoryId,
    placement
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const isWestlakeNba = epn === 'Westlake Financing' && placement === 'nba';
  let ggEventValueCode = valueEventCode.WESTLAKE;
  const analyticsData = {
    'Westlake Financing': {
      cmp: uiContext === 'simplified' ? 'get_loan_btn' : 'get_loan_btn_mid',
      co_txt_url: 'Get Pre-qualified'
    },
    Autofi: {
      cmp: uiContext === 'simplified' ? 'str_ofr_top' : 'str_ofr_btm',
      co_txt_url: 'Start Your Offer'
    },
    CapitalOne: {
      cmp: 'get_loan_btn',
      co_txt_url: 'Get Pre-qualified'
    },
    Roadster: {
      cmp: 'ctn_dl_onl',
      co_txt_url: 'Continue Online - Roadster'
    },
    CarNow: {
      cmp: 'ctn_dl_onl',
      co_txt_url: 'Continue Online - Car Now'
    },
    FordMotorCredit: {
      cmp: uiContext === 'styled' ? 'text-link::saca::apply-for-financing::nds' : 'text-link::saca::apply-for-financing::price-card',
      co_txt_url: 'Apply for Financing'
    }
  };
  if (isWestlakeNba) {
    pixallData.eventSource = 'lead';
    pixallData.leadType = 'credit';
    pixallData.pixallData = {
      clickTypeToListingType: isVDP(dataLayer)
    };
    ggEventValueCode = valueEventCode.CREDIT_APP;
  }
  const eventResult = {
    'Westlake Financing': {
      eventResult: isWestlakeNba ? 'text-link::westlake::get-pre-qualified::nba' : 'cta::finance::westlake'
    },
    Autofi: {
      eventResult: 'finance::autofi'
    },
    CapitalOne: {
      eventResult: 'finance::capital-one'
    },
    Roadster: {
      eventResult: 'cta::continue-online::roadster'
    },
    CarNow: {
      eventResult: 'cta::continue-online::carnow'
    }
  };
  const eventData = {
    'Westlake Financing': {
      eventData: {
        vehicle: googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        })
      },
      vehicles: {
        cj_ga_event_value_code: ggEventValueCode
      }
    }
  };
  const eventElement = {
    'Westlake Financing': {
      eventElement: isWestlakeNba ? 'nds' : 'partner'
    },
    Roadster: {
      eventElement: 'nds'
    },
    CarNow: {
      eventElement: 'nds'
    }
  };
  Pixall.sendClickEvent({
    inventory,
    ...pixallData
  }, dataLayer, option);
  if (epn !== 'FordMotorCredit') {
    Google.sendClickEvent({
      eventElement: 'partner',
      ...eventElement[epn],
      ...eventResult[epn],
      ...eventData[epn]
    }, option);
  }
  return {
    ...analyticsData[epn]
  };
}
export function westlakeCTAImpression(metadata, option) {
  const {
    data,
    dataLayer,
    state,
    history
  } = metadata;
  const pageId = dataLayer?.page?.brandPg ?? dataLayer?.page?.pg ?? '';
  const {
    inventoryId,
    uiContext = 'styled'
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const {
    listingType,
    owner,
    clickType
  } = inventory || {};
  let placement;
  if (uiContext === 'simplified') {
    placement = 'top';
  } else {
    placement = 'nds';
  }
  Google.sendImpressions(metadata, {
    eventElement: 'partner',
    eventResult: `finance::westlake::get-quote::${placement}`,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  Pixall.sendImpression({
    dataLayer,
    history
  }, {
    label: `finance::westlake::get-quote::${placement}`,
    applicationType: 'loan',
    eventSource: 'application',
    eventType: 'impression',
    pageType: 'vdp',
    pageId,
    ...inventoryTransformer.getInventoryData(inventory),
    vehicleType: listingType?.toLowerCase(),
    listingCode: owner?.id,
    clickType
  }, option);
  return {};
}

/**
 * @summary fires Adobe link track for closing a sticky ad
 * @cmp cls_stky_ad
 */
export function closeStickyAdClick(option) {
  const cmp = 'cls_stky_ad';
  Google.sendClickEvent({
    eventElement: 'ad',
    eventResult: 'close-ad'
  }, option);
  return {
    cmp
  };
}

/**
 * @summary Handles Calculator click for AT & KBB
 * @cmp vw_ws
 */
export function windowStickerClick(_ref5, option) {
  let {
    data,
    dataLayer,
    state
  } = _ref5;
  const {
    inventoryId
  } = data;
  const cmp = 'vw_ws';
  const inventory = getInventoryOwnerById(state, inventoryId);
  Pixall.sendClickEvent({
    label: cmp,
    eventSource: 'vehicle',
    eventType: 'click',
    inventory
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'partner',
    eventResult: 'test-link::view-original-window-sticker',
    eventNonInteractive: true
  }, option);
  return {
    cmp
  };
}

/**
 * @summary Handles inspection checklist click
 * @cmp vw_cert_chlst
 */
export function inspectionChecklistClick(_ref6, option) {
  let {
    data,
    dataLayer,
    state
  } = _ref6;
  const {
    inventoryId
  } = data;
  const cmp = 'vw_cert_chlst';
  const inventory = getInventoryOwnerById(state, inventoryId);
  Pixall.sendClickEvent({
    label: cmp,
    eventSource: 'vehicle',
    eventType: 'click',
    inventory
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'partner',
    eventResult: 'test-link::view-inspection-checklist',
    eventNonInteractive: true
  }, option);
  return {
    cmp
  };
}

/**
 * @summary Handles ICO Click in /how-to-buy-cars-online
 *
 */

export function icoInlineTextLinkClick(metadata, option) {
  const {
    data
  } = metadata;
  const {
    ctaCopy
  } = data;
  Google.sendClickEvent({
    eventElement: 'ico',
    eventResult: `text-link::buy-online-help::${sanitizeValue(ctaCopy)}`,
    eventNonInteractive: true
  }, option);
  return {};
}

/**
 * @summary Handles root insurance click on text link
 *
 */
export function rootInsuranceTextLinkClick(metadata, option) {
  const {
    data = {},
    dataLayer = {},
    state
  } = metadata;
  const {
    inventoryId
  } = data;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const additionalResources = inventory.resources?.additionalResources ?? [];
  const partnerResource = additionalResources.find(resource => resource?.cmp === 'get_quote_txt');
  Pixall.sendClickEvent({
    eventSource: 'insurance',
    eventType: 'click',
    inventory,
    vendor: 'root-insurance',
    label: 'text-link_insurance_root-insurance',
    pixallData: {
      clickTypeToListingType: true
    }
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'partner',
    eventAction: 'click',
    eventNonInteractive: false,
    eventResult: partnerResourceHelper(partnerResource)
  }, option);
  return {};
}
export function vhrOverviewImpression(metadata, option) {
  const {
    data,
    dataLayer = {},
    state
  } = metadata;
  const {
    vendor = '',
    inventoryId
  } = data;
  const zip = _get(dataLayer, 'user.zipCode', '');
  const inventory = getInventoryOwnerById(state, inventoryId);
  let label = 'vehicle-history-report';
  if (vendor) {
    label += `::${vendor}`;
  }
  const pixallPayload = {
    clickType: inventory?.clickType || '',
    eventSource: 'vehicle-history-report',
    goal: 'vehicle-history-report',
    includeInventory: true,
    inventory,
    label,
    vendor,
    zip
  };
  Pixall.sendImpression({
    dataLayer
  }, pixallPayload, option);
  return {
    cmp: label
  };
}
export default {
  capOneResourceClick,
  closeStickyAdClick,
  creditReportClick,
  financeWithUsLink,
  kbbInstantCashClick,
  preQualCTAClick,
  printTrack,
  productTileClick,
  sellCarClick,
  shareClick,
  warrantyResourceClick,
  windowStickerClick,
  featuredPartnerImpression,
  featuredPartnerClick,
  kbbLogoClick,
  icoInlineTextLinkClick,
  inspectionChecklistClick,
  westlakeCTAImpression,
  rootInsuranceClick,
  rootInsuranceTextLinkClick,
  vhrOverviewImpression
};